<template>
  <NavBar v-if="userLoaded" pageName="Service Types Settings"></NavBar>

  <v-container style="margin-top: 185px;">
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-btn variant="text" size="small" @click="$router.push('/settings/services')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Services Settings
    </v-btn>

    <!-- Header with actions -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="6">
        <h2>Service Types Management</h2>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadServiceTypes">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn color="success" @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus</v-icon>
          Add Service Type
        </v-btn>
      </v-col>
    </v-row>

    <!-- Service Types Table -->
    <v-card>
      <v-card-title>Service Types: {{ serviceTypes.length }}</v-card-title>
      <v-card-text>
        <v-data-table
          :headers="tableHeaders"
          :items="serviceTypes"
          :loading="loading"
          :items-per-page="20"
          item-value="id"
          class="elevation-1"
        >
          <!-- Active indicator -->
          <template #[`item.active`]="{ item }">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              size="small"
              @click="toggleActive(item)"
              style="cursor: pointer;"
            >
              {{ item.active ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <!-- Description column -->
          <template #[`item.description`]="{ item }">
            {{ item.description || '-' }}
          </template>

          <!-- Show in Search column -->
          <template #[`item.show_in_search`]="{ item }">
            <v-chip
              :color="item.show_in_search ? 'primary' : 'grey'"
              size="small"
              @click="toggleShowInSearch(item)"
              style="cursor: pointer;"
            >
              {{ item.show_in_search ? 'Yes' : 'No' }}
            </v-chip>
          </template>

          <!-- Search Label column -->
          <template #[`item.label_on_search_button`]="{ item }">
            {{ item.label_on_search_button || '-' }}
          </template>

          <!-- Actions column -->
          <template #[`item.actions`]="{ item }">
            <v-btn
              size="x-small"
              color="primary"
              class="mr-1"
              @click="openEditDialog(item)"
              title="Edit service type"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>

          <!-- No data -->
          <template v-slot:no-data>
            <v-alert type="info" class="ma-2">
              No service types found
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Info alert -->
    <v-alert type="info" density="compact" class="mt-4">
      <v-icon class="mr-2">mdi-information</v-icon>
      Only <strong>active</strong> service types will be shown in the "Add New Service" form.
      Click on the status chip to toggle active/inactive.
    </v-alert>

    <!-- Add/Edit Service Type Dialog -->
    <v-dialog v-model="serviceTypeDialog" max-width="500">
      <v-card>
        <v-card-title :class="isEditing ? 'bg-primary text-white' : 'bg-success text-white'">
          <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ isEditing ? 'Edit Service Type' : 'Add Service Type' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="serviceTypeForm">
            <v-text-field
              v-model="formData.name"
              label="Name"
              :rules="[v => !!v || 'Name is required', v => !v || v.length >= 2 || 'Min 2 characters']"
              dense
              outlined
              class="mb-3"
              hint="Internal name used in code"
              persistent-hint
            ></v-text-field>

            <v-textarea
              v-model="formData.description"
              label="Description"
              rows="3"
              dense
              outlined
              class="mb-3"
            ></v-textarea>

            <v-switch
              v-model="formData.active"
              label="Active"
              color="success"
              hint="Only active service types are shown in the Add Service form"
              persistent-hint
              density="compact"
            >
              <template v-slot:label>
                <span class="d-flex align-center">
                  <v-icon class="mr-2" :color="formData.active ? 'success' : 'error'">
                    {{ formData.active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  {{ formData.active ? 'Active' : 'Inactive' }}
                </span>
              </template>
            </v-switch>

            <v-switch
              v-model="formData.show_in_search"
              color="primary"
              density="compact"
              class="mt-3"
            >
              <template v-slot:label>
                <span class="d-flex align-center">
                  <v-icon class="mr-2" :color="formData.show_in_search ? 'primary' : 'grey'">
                    {{ formData.show_in_search ? 'mdi-magnify' : 'mdi-magnify-close' }}
                  </v-icon>
                  {{ formData.show_in_search ? 'Show in Search Filters' : 'Hidden from Search Filters' }}
                </span>
              </template>
            </v-switch>

            <v-text-field
              v-model="formData.label_on_search_button"
              label="Label on Search Button"
              hint="Leave empty to use name"
              persistent-hint
              dense
              outlined
              class="mt-3"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="serviceTypeDialog = false">Cancel</v-btn>
          <v-btn
            :color="isEditing ? 'primary' : 'success'"
            @click="saveServiceType"
            :loading="saving"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { getServiceTypes, addServiceType, updateServiceType } from "@/composables/useServiceTypesAPI";
import { log } from "@/composables/useLog";
import store from "@/store";

export default defineComponent({
  name: 'ServiceTypesSettings',
  components: { NavBar },
  data() {
    return {
      loading: false,
      token: '',
      userLoaded: false,
      serviceTypes: [],

      // Dialog
      serviceTypeDialog: false,
      isEditing: false,
      saving: false,
      formData: {
        id: null,
        name: '',
        description: '',
        active: true,
        show_in_search: false,
        label_on_search_button: ''
      },

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },

      // Table headers
      tableHeaders: [
        { title: 'ID', key: 'id', width: '60px' },
        { title: 'Name', key: 'name' },
        { title: 'Description', key: 'description' },
        { title: 'Status', key: 'active', align: 'center', width: '100px' },
        { title: 'Search', key: 'show_in_search', align: 'center', width: '100px' },
        { title: 'Search Label', key: 'label_on_search_button', width: '150px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '80px' }
      ]
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    async loadServiceTypes() {
      this.loading = true;
      try {
        const result = await getServiceTypes(this.token);
        log("Service types loaded:", result);
        if (!result.error) {
          this.serviceTypes = result.service_types || [];
        } else {
          this.showSnackbar(result.message || 'Error loading service types', 'error');
        }
      } catch (err) {
        log("Error loading service types:", err);
        this.showSnackbar('Error loading service types', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.isEditing = false;
      this.formData = {
        id: null,
        name: '',
        description: '',
        active: true,
        show_in_search: false,
        label_on_search_button: ''
      };
      this.serviceTypeDialog = true;
    },

    openEditDialog(serviceType) {
      this.isEditing = true;
      this.formData = {
        id: serviceType.id,
        name: serviceType.name,
        description: serviceType.description || '',
        active: serviceType.active,
        show_in_search: serviceType.show_in_search,
        label_on_search_button: serviceType.label_on_search_button || ''
      };
      this.serviceTypeDialog = true;
    },

    async saveServiceType() {
      if (!this.formData.name || this.formData.name.length < 2) {
        this.showSnackbar('Please enter a valid name (min 2 characters)', 'error');
        return;
      }

      this.saving = true;
      try {
        let result;
        if (this.isEditing) {
          result = await updateServiceType(this.token, this.formData);
        } else {
          result = await addServiceType(this.token, this.formData);
        }

        if (!result.error) {
          this.showSnackbar(this.isEditing ? 'Service type updated successfully' : 'Service type added successfully');
          this.serviceTypeDialog = false;
          await this.loadServiceTypes();
        } else {
          this.showSnackbar(result.message || 'Error saving service type', 'error');
        }
      } catch (err) {
        log("Error saving service type:", err);
        this.showSnackbar('Error saving service type', 'error');
      } finally {
        this.saving = false;
      }
    },

    async toggleShowInSearch(serviceType) {
      this.loading = true;
      try {
        const result = await updateServiceType(this.token, {
          id: serviceType.id,
          show_in_search: !serviceType.show_in_search
        });

        if (!result.error) {
          this.showSnackbar(`Service type ${!serviceType.show_in_search ? 'shown in' : 'hidden from'} search filters`);
          await this.loadServiceTypes();
        } else {
          this.showSnackbar(result.message || 'Error updating service type', 'error');
        }
      } catch (err) {
        log("Error toggling show_in_search:", err);
        this.showSnackbar('Error updating service type', 'error');
      } finally {
        this.loading = false;
      }
    },

    async toggleActive(serviceType) {
      this.loading = true;
      try {
        const result = await updateServiceType(this.token, {
          id: serviceType.id,
          active: !serviceType.active
        });

        if (!result.error) {
          this.showSnackbar(`Service type ${!serviceType.active ? 'activated' : 'deactivated'} successfully`);
          await this.loadServiceTypes();
        } else {
          this.showSnackbar(result.message || 'Error updating service type', 'error');
        }
      } catch (err) {
        log("Error toggling active status:", err);
        this.showSnackbar('Error updating service type', 'error');
      } finally {
        this.loading = false;
      }
    }
  },

  async created() {
    const { getLoggedIn } = useGetLoggedIn();
    await getLoggedIn();
    this.token = store.getters.getToken;
    this.userLoaded = true;
    await this.loadServiceTypes();
  }
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
